import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DownloadSimple, FileX } from "@phosphor-icons/react";

/*
  Iter 85.57 · Universal attachment preview dialog.
  ────────────────────────────────────────────────
  Shared quick-view surface for every attachment-bearing record type
  (note attachments, engagement/client documents, briefing source
  materials). One canonical file behind Preview AND Download — the
  server just flips Content-Disposition via `?inline=1`.

  Supported previews:
    · PDF        → <iframe> (native browser rendering)
    · Image      → <img> constrained to viewport
    · Text/MD    → fetched text rendered in <pre>
    · CSV        → fetched text in <pre> (partners scan quickly)
    · Everything else (docx/xlsx/pptx/legacy office/binary) → clear
      "Preview unavailable" message + Download CTA.

  Design invariants:
    · Same auth surface as Download (cookie / bearer / ?auth=).
    · No duplicate document records created — Preview and Download
      both stream the same canonical storage_path.
    · No mutation of the attachment.
    · Graceful fallback on missing/deleted/unsupported files.
*/

// Append `inline=1` to the canonical download URL. Preserve any existing
// query params (e.g. `?auth=<token>` for cookie-less contexts) so preview
// works identically to download for share links.
export function toInlineUrl(url) {
  if (!url) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}inline=1`;
}

// Best-effort content-type / extension classifier. Falls back to filename
// extension when the server didn't tag the file (legacy uploads).
function classify({ contentType, filename }) {
  const ct = (contentType || "").toLowerCase();
  const ext = (filename || "").split(".").pop().toLowerCase();
  if (ct === "application/pdf" || ext === "pdf") return "pdf";
  if (ct.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)) return "image";
  if (ct.startsWith("text/") || ["txt", "md", "markdown", "csv", "log", "json", "xml", "yml", "yaml"].includes(ext)) return "text";
  return "unsupported";
}

function fmtBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AttachmentPreviewDialog({
  open,
  onOpenChange,
  url,           // canonical download URL (no ?inline; we add it)
  filename,
  contentType,
  size,
  uploadedBy,
}) {
  const kind = classify({ contentType, filename });
  const inlineUrl = toInlineUrl(url);
  const [textBody, setTextBody] = useState("");
  const [textLoading, setTextLoading] = useState(false);
  const [textError, setTextError] = useState(null);

  // Fetch the raw bytes for text/markdown/csv so we can render them in a
  // controlled <pre> — iframes for text files can trip mixed-content or
  // CSP hiccups depending on how the object storage responds.
  useEffect(() => {
    if (!open) return;
    if (kind !== "text") return;
    let cancelled = false;
    setTextLoading(true);
    setTextError(null);
    setTextBody("");
    fetch(inlineUrl, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((t) => { if (!cancelled) setTextBody(t); })
      .catch((e) => { if (!cancelled) setTextError(e?.message || "Failed to load"); })
      .finally(() => { if (!cancelled) setTextLoading(false); });
    return () => { cancelled = true; };
  }, [open, inlineUrl, kind]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[95vw] h-[90vh] rounded-none bg-card p-0 flex flex-col"
        data-testid="attachment-preview-dialog"
      >
        <DialogHeader className="px-5 py-3 border-b border-border shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <DialogTitle className="font-display text-base font-bold truncate" title={filename}>
                {filename || "Attachment"}
              </DialogTitle>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
                {fmtBytes(size)}{uploadedBy ? ` · ${uploadedBy}` : ""}{contentType ? ` · ${contentType}` : ""}
              </div>
            </div>
            <a
              href={url}
              data-testid="attachment-preview-download"
              className="inline-flex items-center gap-1.5 text-xs uppercase font-mono tracking-widest text-primary hover:underline shrink-0"
              aria-label={`Download ${filename}`}
            >
              <DownloadSimple size={13} weight="bold" /> Download
            </a>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 bg-secondary/20 overflow-hidden">
          {kind === "pdf" && (
            <iframe
              src={inlineUrl}
              title={filename}
              data-testid="attachment-preview-pdf"
              className="w-full h-full border-0 bg-background"
            />
          )}
          {kind === "image" && (
            <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
              <img
                src={inlineUrl}
                alt={filename}
                data-testid="attachment-preview-image"
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )}
          {kind === "text" && (
            <div className="w-full h-full overflow-auto p-5">
              {textLoading && (
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Loading…</div>
              )}
              {textError && !textLoading && (
                <div className="text-xs font-mono uppercase tracking-widest text-destructive">
                  Failed to load preview — {textError}
                </div>
              )}
              {!textLoading && !textError && (
                <pre
                  data-testid="attachment-preview-text"
                  className="text-xs whitespace-pre-wrap font-mono leading-relaxed text-foreground"
                >
                  {textBody}
                </pre>
              )}
            </div>
          )}
          {kind === "unsupported" && (
            <div
              data-testid="attachment-preview-unsupported"
              className="w-full h-full flex flex-col items-center justify-center gap-4 px-8 text-center"
            >
              <FileX size={40} weight="duotone" className="text-muted-foreground" />
              <div className="max-w-md">
                <div className="font-display text-lg font-bold mb-1">Preview unavailable for this file type</div>
                <div className="text-xs text-muted-foreground leading-relaxed">
                  This format can't be rendered safely in the browser. Download the file to view it in its native application.
                </div>
              </div>
              <a
                href={url}
                data-testid="attachment-preview-unsupported-download"
                className="inline-flex items-center gap-1.5 border border-primary/60 px-4 py-2 text-xs uppercase font-mono tracking-widest text-primary hover:bg-primary/10"
              >
                <DownloadSimple size={13} weight="bold" /> Download {filename}
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
